import React from 'react';
import { Tag, Calendar, Phone, Search, MessageSquare } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const releases = [
  {
    title: 'Move Faster, Build Smarter with Universal Search and AI chat',
    date: 'March 01, 2026',
    description: 'Introducing Universal Search and AI-powered guidance to streamline your workflow.',
    features: [
      'Command + K Search: A universal search bar for instant navigation',
      'Assistant Search: Find any pre-built assistant without scrolling',
      'AI Chat: Learn how to build elite agents using platform best practices'
    ],
    tags: ['Feature', 'AI'],
    icon: Search
  },
  {
    title: 'AIConneqtors Telephony is here',
    date: 'January 26, 2026',
    description: 'Get phone numbers for voice agents directly in AIConneqtors.',
    features: [
      'Purchase US (+1) and Indian (+91) phone numbers',
      'Connect each number to a specific agent',
      'Manage expiry and renewals from your dashboard'
    ],
    tags: ['Feature', 'Telephony'],
    icon: Phone
  },
  {
    title: 'Enhanced Call Analytics Dashboard',
    date: 'December 15, 2025',
    description: 'New analytics features to help you understand your voice AI performance.',
    features: [
      'Real-time call monitoring',
      'Sentiment analysis for each conversation',
      'Detailed performance metrics and reports'
    ],
    tags: ['Feature', 'Analytics'],
    icon: MessageSquare
  }
];

const Releases = () => {
  const [activeFilter, setActiveFilter] = React.useState('all');

  return (
    <div className="min-h-screen bg-[#0a0a0f]">
      <Header />
      
      <main className="pt-24 pb-16">
        {/* Hero Section */}
        <div className="max-w-4xl mx-auto px-6 text-center mb-16">
          <div className="inline-block px-4 py-1.5 bg-teal-400/10 border border-teal-400/30 rounded-full text-teal-400 text-sm font-medium mb-6">
            Releases
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Product Updates
          </h1>
          <p className="text-gray-400 text-lg">
            Stay up to date with the latest features, improvements, and fixes to the platform.
          </p>
        </div>

        {/* Filter Tabs */}
        <div className="max-w-4xl mx-auto px-6 mb-12">
          <div className="flex gap-2 justify-center">
            {['all', 'Feature', 'Changelog'].map((filter) => (
              <button
                key={filter}
                onClick={() => setActiveFilter(filter)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  activeFilter === filter
                    ? 'bg-teal-400 text-black'
                    : 'bg-[#1a1a1f] text-gray-300 hover:bg-[#252530]'
                }`}
              >
                {filter === 'all' ? 'All Updates' : filter + ' Release'}
              </button>
            ))}
          </div>
        </div>

        {/* Releases Timeline */}
        <div className="max-w-4xl mx-auto px-6">
          <div className="space-y-8">
            {releases.map((release, index) => (
              <div
                key={index}
                className="bg-[#0d1117] border border-gray-800 rounded-2xl p-8 hover:border-gray-700 transition-colors"
              >
                <div className="flex items-start gap-6">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center flex-shrink-0">
                    <release.icon className="w-7 h-7 text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-xl font-semibold text-white">{release.title}</h3>
                    </div>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="flex items-center gap-1.5 text-gray-500 text-sm">
                        <Calendar className="w-4 h-4" />
                        {release.date}
                      </div>
                      <div className="flex gap-2">
                        {release.tags.map((tag, i) => (
                          <span
                            key={i}
                            className="flex items-center gap-1 px-2 py-0.5 bg-teal-400/10 text-teal-400 text-xs rounded-full"
                          >
                            <Tag className="w-3 h-3" />
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                    <p className="text-gray-400 mb-4">{release.description}</p>
                    <ul className="space-y-2">
                      {release.features.map((feature, i) => (
                        <li key={i} className="flex items-start gap-2 text-gray-300 text-sm">
                          <span className="text-teal-400 mt-1">•</span>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Subscribe Section */}
        <div className="max-w-4xl mx-auto px-6 mt-16">
          <div className="bg-gradient-to-r from-teal-500/20 to-blue-500/20 border border-teal-500/30 rounded-2xl p-8 text-center">
            <h2 className="text-2xl font-bold text-white mb-4">
              Stay Updated
            </h2>
            <p className="text-gray-400 mb-6">
              Subscribe to our newsletter to get notified about new features and updates.
            </p>
            <div className="flex gap-2 max-w-md mx-auto">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-white outline-none focus:border-teal-400"
              />
              <button className="bg-teal-400 hover:bg-teal-500 text-black px-6 py-2.5 rounded-lg font-semibold transition-colors">
                Subscribe
              </button>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Releases;
